import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { WANDZ_PASSIVE_DATA_QUERY } from './operations';
import { formatWandzData } from './utils';

/**
 * WandzPassiveData is a React component that fetches data using a GraphQL query
 * and merges the formatted result into the databridge `window.digitalData` object under the 'wandz' key
 * available to the Wandz AI scripts.
 *
 * This component does not render any UI and is intended for side-effect data fetching only.
 *
 * @component
 * @example
 * <WandzPassiveData />
 *
 * @returns {null} This component does not render anything.
 */

let promise = null;

// This component only fetches passive data and stores it in window.digitalData
function WandzPassiveData() {
  const mergeWandzPassiveData = (newData) => {
    if (!window.digitalData) return;

    const formattedData = formatWandzData(newData);

    if (formattedData && Object.keys(formattedData).length > 0) {
      window.digitalData.merge('wandz.passiveData', formattedData);
    }
  };

  const [fetch, { loading, data }] = useLazyQuery(WANDZ_PASSIVE_DATA_QUERY, {
    ssr: false,
    context: { batch: true },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!loading && data) {
      mergeWandzPassiveData(data);
    }
  }, [data, loading]);

  useEffect(() => {
    if (!promise) promise = fetch().finally(() => { promise = null; });
  }, [fetch]);

  return null;
}

export default WandzPassiveData;
